import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FaCheck, FaPlus, FaMinus, FaClock, FaFileAlt, FaCertificate, FaShieldAlt, FaLink, FaClipboardCheck } from 'react-icons/fa'

const PaymentProcessors = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index)
  }

  const services = [
    {
      icon: <FaClock className="text-4xl text-accent mb-4" />,
      title: 'Real-Time Reserve Verification',
      description: 'Provide real-time transparency to merchants and users with continuous verification of your reserve holdings.'
    },
    {
      icon: <FaFileAlt className="text-4xl text-accent mb-4" />,
      title: 'Financial Statement Audits',
      description: 'Meet regulatory and partner requirements with comprehensive audits conducted by crypto-native certified public accountants.'
    },
    {
      icon: <FaCertificate className="text-4xl text-accent mb-4" />,
      title: 'SOC 2 Type II Reports',
      description: 'Demonstrate the security and reliability of your payment infrastructure to enterprise customers and partners.'
    },
    {
      icon: <FaShieldAlt className="text-4xl text-accent mb-4" />,
      title: 'AML/KYC Compliance',
      description: 'Ensure your compliance programs meet regulatory standards across all jurisdictions where you operate.'
    },
    {
      icon: <FaLink className="text-4xl text-accent mb-4" />,
      title: 'Settlement Verification',
      description: 'Independent verification of settlement processes and reserve adequacy for merchant payouts.'
    },
    {
      icon: <FaClipboardCheck className="text-4xl text-accent mb-4" />,
      title: 'Regulatory Advisory',
      description: 'Navigate complex money transmitter and payment processor regulations with expert guidance.'
    },
  ]

  const growthItems = [
    {
      title: 'Build Trust with Enterprise Merchants and Partners',
      content: 'Enterprise customers demand proof of financial stability and operational security. Professional audits and attestations provide the credibility needed to close deals with major merchants.'
    },
    {
      title: 'Navigate Multi-Jurisdictional Compliance Requirements',
      content: 'Operating as a payment processor across multiple jurisdictions requires compliance with varying regulatory frameworks. Our team helps you maintain compliance while scaling globally.'
    },
    {
      title: 'Secure Banking Relationships and Institutional Partnerships',
      content: 'Traditional financial institutions require professional financial reporting and controls. Demonstrate your commitment to compliance and transparency to maintain critical banking relationships.'
    },
    {
      title: 'Differentiate in a Competitive Market',
      content: 'In a crowded payment processing market, transparency and security are key differentiators. Use independent audits and real-time reserve verification as competitive advantages.'
    },
  ]

  return (
    <>
      <Helmet>
        <title>Payment Processor Audit Services - Proof of Reserves Auditor</title>
        <meta name="description" content="Comprehensive audit and compliance services for cryptocurrency payment processors and FinTech companies." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-primary text-white py-20 overflow-hidden">
          {/* Animated Particle Wave Background */}
          <div className="absolute inset-0 opacity-30">
            <svg className="absolute bottom-0 w-full h-auto" viewBox="0 0 1280 400" preserveAspectRatio="none">
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4A9BFF" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#4A9BFF" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#4A9BFF" stopOpacity="0.3" />
                </linearGradient>
              </defs>

              {/* Multiple wave layers for depth */}
              <path
                d="M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.4"
              >
                <animate
                  attributeName="d"
                  dur="8s"
                  repeatCount="indefinite"
                  values="
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,250 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                />
              </path>

              <path
                d="M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.3"
              >
                <animate
                  attributeName="d"
                  dur="10s"
                  repeatCount="indefinite"
                  values="
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,290 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                />
              </path>

              {/* Dotted particle effect */}
              <g opacity="0.6">
                {[...Array(50)].map((_, i) => {
                  const x = (i * 25.6) % 1280;
                  const y = 150 + (i * 13) % 200;
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r="2"
                      fill="#4A9BFF"
                      opacity="0.5"
                    >
                      <animate
                        attributeName="opacity"
                        values="0.3;0.8;0.3"
                        dur={`${3 + (i % 4)}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="cy"
                        values={`${y};${y - 20};${y}`}
                        dur={`${4 + (i % 3)}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                  )
                })}
              </g>
            </svg>
          </div>
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Payment Processors & FinTech
                <br />
                <span className="text-accent">Audit Services</span>
              </h1>
              <p className="text-2xl text-gray-200 mb-8">
                Build trust and scale with confidence
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Demonstrate Financial Stability to Enterprise Customers
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Meet Regulatory Requirements Across Multiple Jurisdictions
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0" />
                  <p className="text-lg">
                    Secure and Maintain Critical Banking Relationships
                  </p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
              >
                TALK TO AN EXPERT
              </Link>
            </div>
          </div>
        </section>

        {/* The Challenge */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl font-bold text-primary mb-6">
                The Payment Processor Challenge
              </h2>
              <p className="text-xl text-gray-600">
                Cryptocurrency payment processors face unique challenges balancing innovation with compliance, security with user experience, and growth with sustainability.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-4xl mb-4">🏦</div>
                <h3 className="text-xl font-bold text-primary mb-3">Banking Challenges</h3>
                <p className="text-gray-600">
                  Maintaining banking relationships requires demonstrating robust financial controls and transparent operations.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-4xl mb-4">📜</div>
                <h3 className="text-xl font-bold text-primary mb-3">Regulatory Complexity</h3>
                <p className="text-gray-600">
                  Multi-jurisdictional operations mean navigating varying money transmitter laws and compliance requirements.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-4xl mb-4">🤝</div>
                <h3 className="text-xl font-bold text-primary mb-3">Enterprise Sales</h3>
                <p className="text-gray-600">
                  Landing enterprise merchants requires professional audits, security certifications, and demonstrated stability.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-accent mb-12">
              Comprehensive Services for Payment Processors
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-6 rounded-lg hover:shadow-xl transition-shadow">
                  {service.icon}
                  <h3 className="text-xl font-bold mb-3 text-primary">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/contact"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
              >
                DISCUSS YOUR NEEDS
              </Link>
            </div>
          </div>
        </section>

        {/* Growth Accordion */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-accent mb-12">
              Accelerate Your Growth
            </h2>
            
            <div className="max-w-4xl mx-auto space-y-4">
              {growthItems.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-4">{item.title}</span>
                    {openAccordion === index ? (
                      <FaMinus className="text-accent flex-shrink-0" />
                    ) : (
                      <FaPlus className="text-accent flex-shrink-0" />
                    )}
                  </button>
                  
                  {openAccordion === index && (
                    <div className="px-6 pb-6 text-gray-700">
                      <p>{item.content}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <h2 className="text-4xl font-bold text-center text-primary mb-12">
              Why Leading Payment Processors Choose Us
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="text-5xl font-bold text-accent mb-2">7+</div>
                <div className="text-gray-600">Years of Crypto Industry Experience</div>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="text-5xl font-bold text-accent mb-2">24/7</div>
                <div className="text-gray-600">Real-Time Monitoring Available</div>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="text-5xl font-bold text-accent mb-2">100%</div>
                <div className="text-gray-600">Satisfaction Rate</div>
              </div>
            </div>

            <div className="max-w-3xl mx-auto bg-white rounded-lg p-8 shadow-lg">
              <p className="text-sm text-gray-600 mb-2">Payment Processor CFO</p>
              <blockquote className="text-2xl font-bold text-primary">
                "Their crypto-native expertise combined with professional audit standards gave us exactly what we needed to close deals with enterprise merchants."
              </blockquote>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-accent">
              Ready to Scale Your Payment Business?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Let's discuss how professional audits and compliance services can help you win enterprise customers, maintain banking relationships, and scale with confidence.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              SCHEDULE CONSULTATION
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}

export default PaymentProcessors